import React from 'react';
// Loyalty card design data: stamp icon packs and card templates.
// Themes come from gift-cards-v2 (the shared card design system).

import { THEMES } from '../gift-cards-v2/data';

export { THEMES };

// ─── Stamp icon packs ───
// Three voices: clean line icons, emoji, and hand-drawn doodles (the
// playful coffee-shop-card feel). Entries are either { svg } (stroke-based,
// currentColor, 24-box) or { emoji }.

export const STAMP_ICONS = [
  { id: 'scissors', label: 'Scissors', svg: (
    <g><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M20 4 8.1 14.3M14.5 12.5 20 20M8.1 9.7 12 13" /></g>
  ) },
  { id: 'sparkle', label: 'Sparkle', svg: (
    <g><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" /></g>
  ) },
  { id: 'heart', label: 'Heart', svg: (
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
  ) },
  { id: 'star', label: 'Star', svg: (
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  ) },
  { id: 'droplet', label: 'Droplet', svg: (
    <path d="M12 2.7s6.5 7 6.5 11.3a6.5 6.5 0 0 1-13 0C5.5 9.7 12 2.7 12 2.7z" />
  ) },
  { id: 'flower', label: 'Flower', svg: (
    <g><circle cx="12" cy="12" r="3" /><path d="M12 9a3 3 0 1 0-3-3M12 9a3 3 0 1 1 3-3M15 12a3 3 0 1 1 3 3M15 12a3 3 0 1 0 3-3M12 15a3 3 0 1 1-3 3M12 15a3 3 0 1 0 3 3M9 12a3 3 0 1 0-3 3M9 12a3 3 0 1 1-3-3" /></g>
  ) },
  { id: 'crown', label: 'Crown', svg: (
    <path d="M3 18h18M4 18 3 7l5 4 4-7 4 7 5-4-1 11" />
  ) },
  { id: 'lips', label: 'Lips', svg: (
    <path d="M2 12s4-5 10-5c2 0 2.5 1.5 0 1.5C14.5 8.5 15 7 17 7c3.4 0 5 5 5 5s-4 6-10 6S2 12 2 12z" />
  ) },
  { id: 'polish', label: 'Nail polish', svg: (
    <g><path d="M10 2h4v6h-4z" /><path d="M8 8h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" /></g>
  ) },
  { id: 'leaf', label: 'Leaf', svg: (
    <path d="M4 20C4 9 10 4 21 3c0 11-5 17-16 17h-1zM4 20c1-5 4-9 9-12" />
  ) },
  { id: 'sun', label: 'Sun', svg: (
    <g><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" /></g>
  ) },
  { id: 'razor', label: 'Razor', svg: (
    <g><path d="M3 7h18v4H3z" /><path d="M12 11v10M9 21h6" /></g>
  ) },
];

export const EMOJI_ICONS = [
  { id: 'em-nails', label: 'Nails', emoji: '💅' },
  { id: 'em-scissors', label: 'Scissors', emoji: '✂️' },
  { id: 'em-haircut', label: 'Haircut', emoji: '💇‍♀️' },
  { id: 'em-massage', label: 'Massage', emoji: '💆‍♀️' },
  { id: 'em-lipstick', label: 'Lipstick', emoji: '💄' },
  { id: 'em-sparkles', label: 'Sparkles', emoji: '✨' },
  { id: 'em-star', label: 'Star', emoji: '⭐' },
  { id: 'em-heart', label: 'Heart', emoji: '💖' },
  { id: 'em-blossom', label: 'Blossom', emoji: '🌸' },
  { id: 'em-herb', label: 'Herb', emoji: '🌿' },
  { id: 'em-sunny', label: 'Sun', emoji: '☀️' },
  { id: 'em-crown', label: 'Crown', emoji: '👑' },
  { id: 'em-bubbles', label: 'Bubbles', emoji: '🫧' },
  { id: 'em-butterfly', label: 'Butterfly', emoji: '🦋' },
  { id: 'em-coffee', label: 'Coffee', emoji: '☕' },
  { id: 'em-strawberry', label: 'Strawberry', emoji: '🍓' },
  { id: 'em-bagel', label: 'Bagel', emoji: '🥯' },
  { id: 'em-cake', label: 'Cake', emoji: '🍰' },
];

// Wobbly, irregular strokes on purpose: these should read as hand-stamped,
// not geometric.
export const DOODLE_ICONS = [
  { id: 'dd-smile', label: 'Smiley', svg: (
    <g><path d="M12 3.2c5.1-.4 8.6 3.5 8.4 8.3-.2 5-3.7 9-8.2 9.2-4.6.2-8.8-3.6-8.9-8.6C3.2 7.3 7 3.6 12 3.2z" /><path d="M9 10.2c.1-.5.1-1 .1-1.4M15 10.3c0-.5 0-1-.1-1.5" /><path d="M8.2 14.3c2.3 2.3 5.2 2.4 7.6.2" /></g>
  ) },
  { id: 'dd-star', label: 'Star', svg: (
    <path d="M12.2 3.1l2.2 5.4 5.7.4-4.3 4 1.1 5.8-4.8-3.1-5.1 2.8 1.4-5.6-4.1-4.2 5.8-.3z" />
  ) },
  { id: 'dd-heart', label: 'Heart', svg: (
    <path d="M12 20.4C7 16.6 3.6 13 3.9 9.6c.2-2.6 2.2-4.3 4.5-4.2 1.6.1 2.8 1 3.6 2.4.9-1.5 2.2-2.4 3.9-2.3 2.3.1 4.2 2 4.2 4.5 0 3.5-3.6 7-8.1 10.4z" />
  ) },
  { id: 'dd-bolt', label: 'Lightning', svg: (
    <path d="M13.4 2.8 5.6 13.2l4.7.4-1.8 7.6 8.1-10.7-4.8-.5z" />
  ) },
  { id: 'dd-flower', label: 'Flower', svg: (
    <g><circle cx="12" cy="12.4" r="2.4" /><path d="M11.7 9.9c-1.1-2.4-.4-4.7 1.2-5.1 1.6-.4 2.9 1.4 2.1 3.7M14.4 11.5c2.4-1.2 4.8-.6 5.1 1 .4 1.6-1.5 2.9-3.8 2M13 14.8c1.3 2.3.7 4.7-.9 5.2-1.6.4-3-1.4-2.2-3.8M9.6 13.4c-2.3 1.3-4.8.8-5.2-.8-.4-1.6 1.4-3 3.8-2.2" /></g>
  ) },
  { id: 'dd-swirl', label: 'Swirl', svg: (
    <path d="M12.4 12.6c-.3-1.3 1-2.3 2.2-1.8 1.6.6 1.8 2.8.4 4-1.8 1.7-4.8 1.1-6.1-1C7.3 11 8.7 7.5 11.6 6.5c3.6-1.2 7.4 1.2 8.1 5" />
  ) },
  { id: 'dd-spark', label: 'Sparkle', svg: (
    <g><path d="M12 4.2c.4 2.6.7 3.6 1.3 4.4.7.8 1.7 1.2 4.3 1.6-2.6.5-3.6.9-4.3 1.7-.6.8-.9 1.8-1.3 4.3-.4-2.5-.8-3.5-1.4-4.3-.7-.8-1.7-1.2-4.2-1.7 2.5-.4 3.5-.8 4.2-1.6.6-.8 1-1.8 1.4-4.4z" /><path d="M18.6 15.7c.2 1.1.4 1.6.7 2 .3.3.8.5 1.9.8-1.1.2-1.6.4-1.9.8-.3.3-.5.9-.7 1.9-.2-1-.4-1.6-.7-1.9-.3-.4-.8-.6-1.9-.8 1.1-.3 1.6-.5 1.9-.8.3-.4.5-.9.7-2z" /></g>
  ) },
  { id: 'dd-squiggle', label: 'Squiggle', svg: (
    <path d="M3.4 12.6c1.7-2.1 3.3-2.2 4.9-.3 1.6 2 3.2 2 4.8 0 1.6-1.9 3.2-1.9 4.9.1 1 1.2 2 1.5 3 1" />
  ) },
];

export const ICON_PACKS = [
  { id: 'icons', label: 'Icons', icons: STAMP_ICONS },
  { id: 'emoji', label: 'Emoji', icons: EMOJI_ICONS },
  { id: 'doodles', label: 'Hand drawn', icons: DOODLE_ICONS },
];

const ALL_ICONS = [...STAMP_ICONS, ...EMOJI_ICONS, ...DOODLE_ICONS];

export const STAMP_LAYOUTS = [
  { id: 'grid', label: 'Grid' },
  { id: 'row', label: 'Single row' },
];

export const FILL_STYLES = [
  { id: 'stamp-ink', label: 'Pressed ink' },
  { id: 'solid', label: 'Solid' },
  { id: 'sticker', label: 'Sticker' },
];

export const ORIENTATIONS = [
  { id: 'landscape', label: 'Horizontal' },
  { id: 'portrait', label: 'Vertical' },
];

// ─── Stamp shapes ───
// circle = the classic CSS ring; scallop and blob are SVG seals (the
// hand-stamped coffee-card look). Paths live in a 48x48 box.
export const STAMP_SHAPES = [
  { id: 'circle', label: 'Circle' },
  { id: 'scallop', label: 'Scalloped' },
  { id: 'blob', label: 'Organic' },
  { id: 'oval', label: 'Oval' },
  { id: 'sketch', label: 'Hand drawn' },
];

// Circle and oval as paths too, so the sticker style (offset duplicate
// behind the seal) can treat every shape uniformly.
export const CIRCLE_PATH = 'M 43 24 A 19 19 0 1 1 5 24 A 19 19 0 1 1 43 24 Z';
export const OVAL_PATH = 'M 24 9.5 C 35 9.5 43.5 16 43.5 24 C 43.5 32 35 38.5 24 38.5 C 13 38.5 4.5 32 4.5 24 C 4.5 16 13 9.5 24 9.5 Z';

// A 12-lobe wax-seal outline: semicircular bumps between anchor points
// on an inner circle. Generated once, deterministically.
export const SCALLOP_PATH = (() => {
  const lobes = 12;
  const cx = 24;
  const cy = 24;
  const r = 19;
  const bump = r * Math.sin(Math.PI / lobes);
  let d = '';
  for (let i = 0; i < lobes; i += 1) {
    const a0 = (i / lobes) * 2 * Math.PI;
    const a1 = ((i + 1) / lobes) * 2 * Math.PI;
    const x0 = (cx + r * Math.cos(a0)).toFixed(2);
    const y0 = (cy + r * Math.sin(a0)).toFixed(2);
    const x1 = (cx + r * Math.cos(a1)).toFixed(2);
    const y1 = (cy + r * Math.sin(a1)).toFixed(2);
    if (i === 0) d += `M ${x0} ${y0} `;
    d += `A ${bump.toFixed(2)} ${bump.toFixed(2)} 0 0 1 ${x1} ${y1} `;
  }
  return `${d}Z`;
})();

export const BLOB_PATH = 'M 24 5.5 C 32 4.5 40.5 9 42 17.5 C 43.5 26 40.5 36 32.5 40.5 C 25 44.5 14.5 43 9 36.5 C 4 30.5 4.5 20 9.5 13.5 C 13.5 8.5 18 6.5 24 5.5 Z';

// Hand-drawn ring (the sketchy coffee-card outline)
export const SKETCH_PATH = 'M 24 6.2 C 33.8 5 42.5 12.5 42 23.2 C 41.6 33.4 33.5 41.6 23.4 41.8 C 13.6 42 5.6 33.8 6.1 23.6 C 6.6 13.8 14.6 7.4 24 6.2 Z';

export const SLOT_LABELS = [
  { id: 'icon', label: 'Icons' },
  { id: 'number', label: 'Numbers' },
];

export const STAMP_BLOCKS = [
  { id: 'full', label: 'Full width' },
  { id: 'left', label: 'Left half' },
  { id: 'right', label: 'Right half' },
];

// Line-art illustrations for the trade (inserted as image elements, in
// an ink that suits the current card surface).
const ILL_SVGS = [
  { id: 'ill-scissors', label: 'Scissors & comb', art: '<circle cx="14" cy="50" r="7"/><circle cx="14" cy="74" r="7"/><path d="M50 30 21 56M34 51 50 76M21 68l8-6"/><path d="M62 28v52M70 28v52M62 36h8M62 46h8M62 56h8M62 66h8"/>' },
  { id: 'ill-dryer', label: 'Hairdryer', art: '<path d="M20 34h36c10 0 18 7 18 15s-8 15-18 15l-8 22c-1 3-5 4-7 1l-9-23H20c-3 0-5-2-5-5V39c0-3 2-5 5-5z"/><path d="M26 42v14M34 42v14M42 42v14"/>' },
  { id: 'ill-pole', label: 'Barber pole', art: '<rect x="34" y="20" width="32" height="62" rx="14"/><path d="M34 34l32-10M34 48l32-10M34 62l32-10M34 76l32-10"/><path d="M40 12h20M40 90h20"/>' },
  { id: 'ill-polish', label: 'Nail polish', art: '<path d="M42 14h16v22H42z"/><path d="M36 36h28c4 0 7 3 7 7v32c0 6-5 11-11 11H40c-6 0-11-5-11-11V43c0-4 3-7 7-7z"/><path d="M50 52v18"/>' },
  { id: 'ill-mirror', label: 'Hand mirror', art: '<circle cx="50" cy="38" r="24"/><circle cx="50" cy="38" r="17"/><path d="M46 62l-4 26c0 3 2 5 5 5h6c3 0 5-2 5-5l-4-26"/>' },
  { id: 'ill-lotus', label: 'Spa bowl', art: '<path d="M16 56c0 16 15 28 34 28s34-12 34-28z"/><path d="M50 50c-6-10-2-22 0-26 2 4 6 16 0 26zM38 52c-8-6-9-16-8-21 4 2 12 9 8 21zM62 52c8-6 9-16 8-21-4 2-12 9-8 21z"/>' },
];
export const ILLUSTRATIONS = ILL_SVGS.map((i) => ({
  ...i,
  toDataUrl: null,
}));
export const illustrationDataUrl = (id, colour) => {
  const entry = ILL_SVGS.find((i) => i.id === id) || ILL_SVGS[0];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none" stroke="${colour}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">${entry.art}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export const HEADER_STYLES = [
  { id: 'classic', label: 'Classic' },
  { id: 'centre', label: 'Centred' },
  { id: 'none', label: 'Custom text' },
];

// One-click conversion: turn the built-in header into editable text
// elements so the salon can restyle or delete every word (Canva-style).
export const headerToElements = (salonName, isStamps, scheme) => ([
  { id: 'hx-tag', type: 'text', content: 'LOYALTY', font: 'inter', fontSize: 15, bold: true, color: '', x: 50, y: 10, scale: 1, rotation: 0 },
  { id: 'hx-title', type: 'text', content: salonName || 'Your Salon', font: 'fraunces', fontSize: 44, bold: true, color: '', x: 50, y: 19, scale: 1, rotation: 0 },
  { id: 'hx-tagline', type: 'text', content: isStamps ? '1 visit = 1 stamp' : `${(scheme && scheme.pointsPerPound) || 1} point per £1`, font: 'inter', fontSize: 15, bold: false, color: '', x: 50, y: 28, scale: 1, rotation: 0 },
]);

// Custom layer (background override + free decorations) — same element
// model as the gift-card designer, so its canvas machinery drives ours.
export const blankCustom = () => ({
  bgMode: null, // null = use the theme colour; 'solid'|'gradient'|'photo'|'pattern' override it
  bgColor: '#29282D',
  bgGradient: { from: '#E8B4C8', to: '#F0654B', angle: 135 },
  bgPhoto: null,
  bgPattern: { occasion: 'birthday' },
  elements: [],
});

// Card templates: full design presets with a point of view, previewed as
// real mini cards in the designer. Templates deliberately leave the
// salon's orientation and stamp-count choices alone.
export const CARD_TEMPLATES = [
  // ── Full-width classics ──
  { id: 'noir', name: 'Noir', design: { themeId: 'noir', icon: 'sparkle', stampShape: 'circle', header: 'classic', fillStyle: 'stamp-ink', stampBlock: 'full' } },
  { id: 'velvet', name: 'Velvet', design: { themeId: 'plum', icon: 'crown', stampShape: 'circle', header: 'classic', fillStyle: 'stamp-ink', stampBlock: 'full' } },
  { id: 'golden', name: 'Golden hour', design: { themeId: 'amber', icon: 'sun', stampShape: 'scallop', header: 'classic', fillStyle: 'stamp-ink', stampBlock: 'full' } },

  // ── Centred hero (big title, stamps below) ──
  { id: 'cafe', name: 'Café stamp', design: {
    themeId: 'cream', icon: 'em-coffee', stampShape: 'scallop', header: 'none', fillStyle: 'solid', stampBlock: 'full',
    custom: { bgMode: null, elements: [
      { id: 'tc-eyebrow', type: 'text', content: 'LOYALTY CARD', font: 'inter', fontSize: 14, bold: true, color: '#B34A2C', x: 50, y: 10, scale: 1, rotation: 0 },
      { id: 'tc-title', type: 'text', content: '{salon}', font: 'fraunces', fontSize: 50, bold: true, color: '#2B1B0E', x: 50, y: 19, scale: 1, rotation: 0 },
      { id: 'tc-tag', type: 'text', content: 'Collect the stamps, the treat is on us', font: 'cormorant', fontSize: 19, italic: true, color: '#6B4A33', x: 50, y: 28.5, scale: 1, rotation: 0 },
    ] },
  } },
  { id: 'candy-pop', name: 'Candy pop', design: {
    themeId: 'rose', icon: 'em-heart', stampShape: 'scallop', header: 'none', fillStyle: 'sticker', stampBlock: 'full',
    custom: { bgMode: 'gradient', bgColor: '#F6E7EE', bgGradient: { from: '#F9DEE9', to: '#F4C2D7', angle: 160 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tcp-title', type: 'text', content: '{salon}', font: 'dmserif', fontSize: 50, bold: false, color: '#B3304A', shadow: true, shadowColor: 'rgba(179,48,74,0.25)', x: 50, y: 15, scale: 1, rotation: 0 },
      { id: 'tcp-tag', type: 'text', content: '1 visit = 1 stamp', font: 'inter', fontSize: 14, bold: true, color: '#9A4B62', x: 50, y: 26, scale: 1, rotation: 0 },
    ] },
  } },
  { id: 'midnight-disco', name: 'Midnight disco', design: {
    themeId: 'midnight', icon: 'em-sparkles', stampShape: 'circle', header: 'centre', fillStyle: 'solid', stampBlock: 'full',
    custom: { bgMode: 'gradient', bgColor: '#1E1B2E', bgGradient: { from: '#3C1F3A', to: '#1E1B2E', angle: 150 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tpl-md-1', type: 'text', content: '✦', fontSize: 26, color: '#E8A838', x: 9, y: 14, scale: 1, rotation: 0 },
      { id: 'tpl-md-2', type: 'text', content: '✧', fontSize: 18, color: '#E8B4C8', x: 91, y: 13, scale: 1, rotation: 0 },
    ] },
  } },
  { id: 'diner', name: 'Retro diner', design: {
    themeId: 'coral', icon: 'star', stampShape: 'oval', header: 'none', fillStyle: 'sticker', stampBlock: 'full',
    custom: { bgMode: 'solid', bgColor: '#D8442B', elements: [
      { id: 'td-title', type: 'text', content: '{salon}', font: 'system', fontSize: 52, bold: true, color: '#F3EADD', shadow: true, shadowColor: '#7A1F0E', x: 50, y: 15, scale: 1, rotation: 0 },
      { id: 'td-tag', type: 'text', content: 'THE INNER CIRCLE', font: 'inter', fontSize: 15, bold: true, color: '#F3EADD', x: 50, y: 26, scale: 1, rotation: 0 },
    ] },
  } },

  // ── Split layout: stamps one half, big art the other (the standout look) ──
  { id: 'starstruck', name: 'Starstruck', design: {
    themeId: 'noir', icon: 'dd-bolt', stampShape: 'circle', header: 'classic', fillStyle: 'solid', stampBlock: 'left',
    custom: { bgMode: 'gradient', bgColor: '#8FB57E', bgGradient: { from: '#A6CB97', to: '#6E9A63', angle: 145 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tpl-ss-1', type: 'shape', shape: 'star', fill: '#EFCB4F', x: 74, y: 54, scale: 2.6, rotation: -6 },
      { id: 'tpl-ss-2', type: 'shape', shape: 'star', fill: '#EFCB4F', x: 92, y: 70, scale: 1.5, rotation: 12 },
    ] },
  } },
  { id: 'sweetheart', name: 'Sweetheart', design: {
    themeId: 'rose', icon: 'dd-heart', stampShape: 'scallop', header: 'classic', fillStyle: 'sticker', stampBlock: 'left',
    custom: { bgMode: 'gradient', bgColor: '#F0C2D2', bgGradient: { from: '#F4CEDB', to: '#E29BB4', angle: 140 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tpl-sw-1', type: 'shape', shape: 'heart', fill: '#B3304A', x: 75, y: 54, scale: 2.4, rotation: -6 },
      { id: 'tpl-sw-2', type: 'shape', shape: 'heart', fill: '#7A2847', x: 92, y: 72, scale: 1.3, rotation: 10 },
    ] },
  } },
  { id: 'matcha', name: 'Matcha', design: {
    themeId: 'sage', icon: 'dd-flower', stampShape: 'blob', header: 'classic', fillStyle: 'solid', stampBlock: 'right',
    custom: { bgMode: 'gradient', bgColor: '#9CC089', bgGradient: { from: '#B6D3A5', to: '#7FA76E', angle: 140 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tpl-ma-1', type: 'shape', shape: 'circle', fill: '#2E3F1A', x: 24, y: 54, scale: 2.2, rotation: 0 },
      { id: 'tpl-ma-2', type: 'text', content: '✿', fontSize: 30, color: '#EBF1DF', x: 24, y: 54, scale: 1, rotation: 0 },
      { id: 'tpl-ma-3', type: 'shape', shape: 'circle', fill: '#3F5527', x: 9, y: 72, scale: 0.9, rotation: 0 },
    ] },
  } },
  { id: 'sunset-club', name: 'Sunset club', design: {
    themeId: 'sunset', icon: 'sun', stampShape: 'circle', header: 'classic', fillStyle: 'solid', stampBlock: 'right',
    custom: { bgMode: 'gradient', bgColor: '#EC8C55', bgGradient: { from: '#F0654B', to: '#E8A838', angle: 135 }, bgPhoto: null, bgPattern: { occasion: 'birthday' }, elements: [
      { id: 'tpl-sc-1', type: 'shape', shape: 'circle', fill: '#F8E6C7', x: 22, y: 53, scale: 2.4, rotation: 0 },
      { id: 'tpl-sc-2', type: 'shape', shape: 'star', fill: '#FBE5B3', x: 8, y: 74, scale: 1.1, rotation: 8 },
    ] },
  } },

  // ── More single-tone options ──
  { id: 'ink-paper', name: 'Ink & paper', design: { themeId: 'bone', icon: 'dd-star', stampShape: 'blob', header: 'classic', fillStyle: 'stamp-ink', stampBlock: 'full' } },
  { id: 'riviera', name: 'Riviera', design: { themeId: 'ocean', icon: 'droplet', stampShape: 'circle', header: 'centre', fillStyle: 'solid', stampBlock: 'full' } },
];

export const defaultDesign = () => ({
  themeId: 'noir',
  icon: 'sparkle',
  layout: 'grid',
  fillStyle: 'stamp-ink',
  orientation: 'landscape',
  stampShape: 'circle',
  header: 'classic',
});

export const themeById = (id) => THEMES.find((t) => t.id === id) || THEMES[0];
export const iconById = (id) => ALL_ICONS.find((i) => i.id === id) || STAMP_ICONS[0];
export const packForIcon = (id) => (ICON_PACKS.find((p) => p.icons.some((i) => i.id === id)) || ICON_PACKS[0]).id;

// Fill the {salon} placeholder in a template's text elements.
export const materialiseTemplate = (design, salonName) => ({
  ...design,
  custom: design.custom ? {
    ...design.custom,
    elements: (design.custom.elements || []).map((el) => (el.type === 'text' && el.content && el.content.includes('{salon}')
      ? { ...el, content: el.content.replace('{salon}', salonName || 'Your Salon') }
      : el)),
  } : design.custom,
});
